import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import FreeShippingProgress, { FREE_STANDARD_THRESHOLD } from './FreeShippingProgress';

export default function CartSheet({ onClose }) {
  const { cart, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4" data-testid="empty-cart">
        <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <p className="text-lg font-heading font-medium text-foreground mb-1">Your cart is empty</p>
        <p className="text-sm text-muted-foreground mb-6">Add research products to get started.</p>
        <Button onClick={() => { onClose(); navigate('/catalog'); }} data-testid="browse-catalog-button">Browse Catalog</Button>
      </div>
    );
  }

  // Standard Shipping is FREE at $100+ subtotal. At $175+ Free 2-Day kicks in,
  // so we promote that label instead.
  const isFreeTwoDay = cart.total >= 175;
  const standardShippingCost = cart.total >= FREE_STANDARD_THRESHOLD ? 0 : 9.99;
  const standardLabel = isFreeTwoDay
    ? '2 Day Shipping (2-3 days)'
    : 'Standard Shipping (3-5 days)';
  const grandTotal = cart.total + standardShippingCost;

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="cart-sheet-content">
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cart.items.map((item) => (
          <div key={`${item.product_id}-${item.variant || ''}`} className="flex gap-3 p-3 rounded-lg bg-muted/30" data-testid={`cart-item-${item.product_id}`}>
            <div className="w-16 h-16 rounded-md bg-secondary/20 overflow-hidden flex-shrink-0">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
              {item.variant && <p className="text-xs text-muted-foreground">{item.variant}</p>}
              {item.bulk_discount_percent > 0 ? (
                <p className="text-sm" data-testid={`cart-item-price-${item.product_id}`}>
                  <span className="line-through text-muted-foreground">${item.original_price.toFixed(2)}</span>
                  <span className="ml-1.5 font-medium text-green-600">${item.price.toFixed(2)}</span>
                  <span className="ml-1 text-xs text-green-600">(-{item.bulk_discount_percent}%)</span>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
              )}
              {item.next_bulk_qty > 0 && (
                <p
                  className="mt-1 text-[11px] font-medium text-[hsl(210,45%,35%)] bg-[hsl(210,45%,95%)] rounded px-1.5 py-0.5 inline-block"
                  data-testid={`cart-bulk-hint-${item.product_id}`}
                >
                  Add {item.next_bulk_qty} more for {item.next_bulk_percent}% off
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variant)} data-testid={`decrease-qty-${item.product_id}`}><Minus className="h-3 w-3" /></Button>
                <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variant)} data-testid={`increase-qty-${item.product_id}`}><Plus className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-destructive hover:text-destructive" onClick={() => removeItem(item.product_id, item.variant)} data-testid={`remove-item-${item.product_id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
            <p className="text-sm font-semibold text-foreground whitespace-nowrap">${item.subtotal.toFixed(2)}</p>
          </div>
        ))}
      </div>
      <div className="pt-4 border-t space-y-3">
        <FreeShippingProgress subtotal={cart.total} />
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground" data-testid="cart-subtotal">${cart.total.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">{standardLabel}</span>
          <span className={standardShippingCost === 0 ? 'text-green-600 font-medium' : 'text-foreground'} data-testid="cart-shipping">
            {standardShippingCost === 0 ? 'FREE' : `$${standardShippingCost.toFixed(2)}`}
          </span>
        </div>
        <p className="text-xs text-muted-foreground italic" data-testid="cart-shipping-note">
          Faster shipping options available at checkout.
        </p>
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="text-sm font-medium text-foreground">Estimated Total</span>
          <span className="text-lg font-heading font-semibold text-foreground" data-testid="cart-total">${grandTotal.toFixed(2)}</span>
        </div>
        <Button className="w-full mt-3 bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" size="lg" onClick={() => { onClose(); navigate('/checkout'); }} data-testid="checkout-button">Proceed to Checkout</Button>
      </div>
    </div>
  );
}
